'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Layers01Icon, RefreshIcon, Shield01Icon } from '@terra/ui-icons';
import type { WebPageFeatureReturnDto } from '@terra/shared-contracts-web-pages';
import { Button } from '../../../components/button';
import { Skeleton } from '../../../components/skeleton';
import { Splitter } from '../../../components/splitter';
import { Spinner } from '../../../components/spinner';
import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';
import { Scroll } from '../../Scroll';
import { AssetTabDrawerEmptyState } from '../assets-tab-drawer/AssetTabDrawerEmptyState';
import { useWebPageFeatures } from '../hooks/useWebPageFeatures';
import { FeatureTypeBadge } from './FeatureTypeBadge';
import { FeatureDetailPane } from './FeatureDetailPane';
import type { FeatureRelatedRequestActions } from './FeatureRelatedRequestsSection';
import {
  canUnblockFeature,
  featureCoverageDotColor,
  getFeatureCoverage,
  isFeatureOverrideRunning,
} from './feature-analysis.utils';

type WebPageFeaturesTabProps = {
  webpageId: string;
  webpageScreenshotUrl?: string | null;
  relatedRequestActions?: FeatureRelatedRequestActions;
};

const OVERRIDE_ACKNOWLEDGE_TIMEOUT_MS = 90_000;
const BLOCKED_CONTROL_MIN_WIDTH = '4.5rem';
const BLOCKED_CONTROL_HEIGHT = '7';

const FeatureListRow = ({
  feature,
  isSelected,
  isOverrideStarting,
  onSelect,
  onUnblockRequested,
}: {
  feature: WebPageFeatureReturnDto;
  isSelected: boolean;
  isOverrideStarting: boolean;
  onSelect: (feature: WebPageFeatureReturnDto) => void;
  onUnblockRequested: (feature: WebPageFeatureReturnDto) => void;
}) => {
  const t = useTranslations('designSystem.applicationAssets.drawer.features');
  const [isBlockedControlActive, setIsBlockedControlActive] = useState(false);
  const coverage = getFeatureCoverage(feature);
  const isOverrideInProgress =
    isOverrideStarting || isFeatureOverrideRunning(feature);
  const canRunOverride = canUnblockFeature({ feature, isOverrideInProgress });
  const isBlockedControlSlotVisible =
    coverage === 'blocked' || isOverrideInProgress;
  const isUnblockVisible = canRunOverride && isBlockedControlActive;

  return (
    <Box
      role="button"
      tabIndex={0}
      w="full"
      textAlign="left"
      px={3}
      py={2.5}
      borderBottomWidth={1}
      borderColor="border.subtle"
      bg={isSelected ? 'bg.subtle' : 'transparent'}
      cursor="pointer"
      onClick={() => onSelect(feature)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(feature);
        }
      }}
      _hover={{ bg: 'bg.subtle' }}
    >
      <HStack gap={2} alignItems="center" justifyContent="space-between">
        <HStack gap={2} alignItems="center" minW={0} flex={1}>
          <Box
            w="8px"
            h="8px"
            borderRadius="full"
            bg={featureCoverageDotColor[coverage]}
            flexShrink={0}
          />
          <BodyDefault color="fg.default" lineClamp={1}>
            {feature.description}
          </BodyDefault>
        </HStack>
        <HStack gap={1.5} alignItems="center" flexShrink={0}>
          <FeatureTypeBadge featureType={feature.featureType} />
          {isBlockedControlSlotVisible && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexShrink={0}
              h={BLOCKED_CONTROL_HEIGHT}
              minW={
                isOverrideInProgress ? undefined : BLOCKED_CONTROL_MIN_WIDTH
              }
              onMouseEnter={() => setIsBlockedControlActive(true)}
              onMouseLeave={() => setIsBlockedControlActive(false)}
              onFocus={() => setIsBlockedControlActive(true)}
              onBlur={() => setIsBlockedControlActive(false)}
            >
              {isOverrideInProgress ? (
                <Spinner size="xs" aria-label={t('lastRunRunning')} />
              ) : isUnblockVisible ? (
                <Button
                  size="xs"
                  h="7"
                  px="2.5"
                  onClick={(event) => {
                    event.stopPropagation();
                    onUnblockRequested(feature);
                  }}
                >
                  <LabelDefault as="span">
                    {t('overrideRun.button')}
                  </LabelDefault>
                </Button>
              ) : (
                <HStack gap={1} color="fg.muted" alignItems="center">
                  <Shield01Icon size={12} />
                  <MetaDefault>{t('blocked')}</MetaDefault>
                </HStack>
              )}
            </Box>
          )}
        </HStack>
      </HStack>
    </Box>
  );
};

export function WebPageFeaturesTab({
  webpageId,
  webpageScreenshotUrl,
  relatedRequestActions,
}: WebPageFeaturesTabProps) {
  const t = useTranslations('designSystem.applicationAssets.drawer.features');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
    null,
  );
  const [overrideDialogFeatureId, setOverrideDialogFeatureId] = useState<
    string | null
  >(null);
  const [overrideStartedAtByFeatureId, setOverrideStartedAtByFeatureId] =
    useState(() => new Map<string, number>());
  const overrideAcknowledgeTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  const handleFeatureOverrideAcknowledged = useCallback((featureId: string) => {
    const pendingTimer = overrideAcknowledgeTimersRef.current.get(featureId);

    if (pendingTimer) {
      clearTimeout(pendingTimer);
      overrideAcknowledgeTimersRef.current.delete(featureId);
    }

    setOverrideStartedAtByFeatureId((currentStartedAt) => {
      if (!currentStartedAt.has(featureId)) {
        return currentStartedAt;
      }

      const nextStartedAt = new Map(currentStartedAt);

      nextStartedAt.delete(featureId);

      return nextStartedAt;
    });
  }, []);
  const { features, isLoading, error, refetch } = useWebPageFeatures({
    webpageId,
    overrideStartedAtByFeatureId,
    onFeatureOverrideAcknowledged: handleFeatureOverrideAcknowledged,
  });
  const handleFeatureOverrideStarted = useCallback(
    (featureId: string) => {
      setOverrideStartedAtByFeatureId((currentStartedAt) =>
        new Map(currentStartedAt).set(featureId, Date.now()),
      );
      overrideAcknowledgeTimersRef.current.set(
        featureId,
        setTimeout(
          () => handleFeatureOverrideAcknowledged(featureId),
          OVERRIDE_ACKNOWLEDGE_TIMEOUT_MS,
        ),
      );
    },
    [handleFeatureOverrideAcknowledged],
  );

  const handleUnblockRequested = useCallback(
    (feature: WebPageFeatureReturnDto) => {
      setSelectedFeatureId(feature.id);
      setOverrideDialogFeatureId(feature.id);
    },
    [],
  );
  const handleOverrideDialogRequestHandled = useCallback(
    () => setOverrideDialogFeatureId(null),
    [],
  );

  useEffect(() => {
    const timers = overrideAcknowledgeTimersRef.current;

    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (features.length === 0) {
      setSelectedFeatureId(null);

      return;
    }

    const selectedStillExists = features.some(
      (feature) => feature.id === selectedFeatureId,
    );

    if (!selectedStillExists) {
      setSelectedFeatureId(features[0].id);
    }
  }, [features, selectedFeatureId]);
  const selectedFeature = useMemo(
    () => features.find((feature) => feature.id === selectedFeatureId) ?? null,
    [features, selectedFeatureId],
  );

  if (isLoading) {
    return (
      <HStack h="full" gap={0} alignItems="stretch">
        <Box w="40%" borderRightWidth={1} borderColor="border.subtle" p={3}>
          <Skeleton h="16px" mb={3} />
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} h="40px" mb={2} />
          ))}
        </Box>
        <Box flex={1} p={4}>
          <Skeleton h="28px" mb={4} w="60%" />
          <Skeleton h="220px" mb={4} />
          <Skeleton h="80px" />
        </Box>
      </HStack>
    );
  }

  if (error) {
    return (
      <VStack gap={4} p={4} alignItems="stretch">
        <AssetTabDrawerEmptyState
          icon={Layers01Icon}
          title={t('errorTitle')}
          description={t('errorDescription')}
        />
        <Box display="flex" justifyContent="center">
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            <RefreshIcon size={14} />
            {t('retry')}
          </Button>
        </Box>
      </VStack>
    );
  }

  if (features.length === 0) {
    return (
      <Box p={4}>
        <AssetTabDrawerEmptyState
          icon={Layers01Icon}
          title={t('emptyTitle')}
          description={t('emptyDescription')}
        />
      </Box>
    );
  }

  return (
    <Splitter.Root
      orientation="horizontal"
      h="full"
      w="full"
      defaultSize={[38, 62]}
      panels={[{ id: 'feature-list' }, { id: 'feature-detail' }]}
      borderWidth="0"
      gap={0}
    >
      <Splitter.Panel id="feature-list" borderWidth="0" h="full" w="full">
        <Scroll orientation="vertical" h="full">
          {features.map((feature) => (
            <FeatureListRow
              key={feature.id}
              feature={feature}
              isSelected={feature.id === selectedFeatureId}
              isOverrideStarting={overrideStartedAtByFeatureId.has(feature.id)}
              onSelect={(nextFeature) => setSelectedFeatureId(nextFeature.id)}
              onUnblockRequested={handleUnblockRequested}
            />
          ))}
        </Scroll>
      </Splitter.Panel>

      <Splitter.ResizeTrigger
        id="feature-list:feature-detail"
        width="1px"
        minWidth="1px"
        bg="border.subtle"
        cursor="col-resize"
        _hover={{ bg: 'border.default' }}
      />

      <Splitter.Panel id="feature-detail" borderWidth="0" h="full" w="full">
        <FeatureDetailPane
          feature={selectedFeature}
          webpageId={webpageId}
          webpageScreenshotUrl={webpageScreenshotUrl}
          relatedRequestActions={relatedRequestActions}
          isOverrideStarting={
            selectedFeature
              ? overrideStartedAtByFeatureId.has(selectedFeature.id)
              : false
          }
          isOverrideDialogRequested={
            selectedFeature
              ? overrideDialogFeatureId === selectedFeature.id
              : false
          }
          onFeatureOverrideStarted={handleFeatureOverrideStarted}
          onOverrideDialogRequestHandled={handleOverrideDialogRequestHandled}
        />
      </Splitter.Panel>
    </Splitter.Root>
  );
}
